<template>
  <div class="listing">
    <loader class="imageLoader" :loading="loading" />
    <pop-header v-model="searchParams" :lang="lang"></pop-header>
    <section class="page">
      <header class="pageHeader">
        <div class="pageHeader__Inner">
          <h1 class="pageHeader__Title">
            {{ $store.getters.getStringTranslation(title.toLowerCase().replace(/\s/g, '')) }}
          </h1>

          <ul class="pageHeader__Meta">
            <li v-for="button in buttons" :key="button.id">
              <router-link class="button" :to="{ name: button.route }" :key="button.id">{{
                $store.getters.getStringTranslation(button.label.toLowerCase().replace(/\s/g, ''))
              }}</router-link>
            </li>
          </ul>
        </div>
      </header>

      <transition name="fade">
        <router-view :searchParams="searchParams" :lang="lang" :types="types" v-if="loading === false"></router-view>
      </transition>

      <div class="tab">
        <div class="tab__MenuWrapper">
          <nav class="tab__Menu">
            <ul class="tab__MenuList--Small">
              <filter-type
                :item="{
                  label: 'All',
                  value: allStatuses,
                }"
                :searchParams="searchParams"
                :param="'status'"
                :filter="filter"
              ></filter-type>
              <filter-type
                v-for="item in filters.status"
                :item="item"
                :searchParams="searchParams"
                :param="'status'"
                :filter="filter"
                :key="item.id"
              ></filter-type>
            </ul>

            <ul class="tab__MenuList--Icons">
              <li>
                <a
                  :class="{ 'is-Active': searchParams.is_featured === undefined }"
                  @click="removeFilter('is_featured')"
                >
                  <icon name="star-half"></icon>
                </a>
              </li>
              <filter-type
                v-for="item in filters.featured"
                :item="item"
                :searchParams="searchParams"
                :param="'is_featured'"
                :filter="filter"
                :key="item.id"
              ></filter-type>
            </ul>

            <ul class="tab__MenuList--Order">
              <filter-type
                v-for="item in filters.order_by"
                :item="item"
                :searchParams="searchParams"
                :param="'order_by'"
                :filter="filter"
                :key="item.id"
              ></filter-type>

              <filter-type
                v-for="item in filters.order_dir"
                :item="item"
                :searchParams="searchParams"
                :param="'order_dir'"
                :filter="filter"
                :key="item.id"
              ></filter-type>
            </ul>
          </nav>

          <nav class="tab__Menu">
            <div class="tab__MenuList--Dropdown">
              <select class="formSelect--Listing" @change="changeLanguage">
                <option :value="item.value" v-for="item in filters.lang" :key="item.value">
                  {{ item.label }}
                </option>
              </select>
            </div>

            <ul class="tab__MenuList--Right">
              <filter-date
                v-for="item in filters.date"
                :item="item"
                :searchParams="searchParams"
                :filter="filter"
                :key="item.id"
              ></filter-date>
            </ul>
          </nav>
        </div>

        <div class="tab__Wrapper">
          <div class="tab__Item">
            <div class="tab__Content">
              <table
                class="postsTable"
                :class="{ 'has-Selected': selected.length > 0 }"
                v-if="items.resources.length > 0 && loading === false"
              >
                <thead>
                  <tr>
                    <th><input type="checkbox" v-model="selectAll" id="selectAll" /></th>

                    <template v-for="cell in cells">
                      <th v-if="cell.class === 'icon'" :class="cell.class" :key="cell.id">
                        <icon :name="cell.label"></icon>
                      </th>

                      <th v-else-if="typeof cell.label === 'string'" :class="cell.class" :key="cell.id">
                        {{
                          $store.getters.getStringTranslation(cell.label.replace(/\s/g, '').toLowerCase()) || cell.label
                        }}
                      </th>

                      <th v-else-if="cell.class === 'postsTable__Languages'" :class="cell.class" :key="cell.id">
                        <icon name="flag" />
                      </th>
                    </template>
                  </tr>
                </thead>
                <tbody v-for="(item, index) in items.resources" :key="item.id">
                  <item
                    :class="{ 'has-Children': item.children.length > 0 }"
                    :item="item"
                    :searchParams="searchParams"
                    :index="index"
                    :selected="selected"
                    :lang="lang"
                    :editor="editor"
                    :buttons="buttons"
                    :cells="cells"
                    @input="select"
                  ></item>

                  <template v-for="(child, index) in item.children">
                    <item
                      class="is-Child"
                      :parent="item"
                      :item="child"
                      :searchParams="searchParams"
                      :index="`child-${index}`"
                      :key="`child-${child.id}`"
                      :selected="selected"
                      :lang="lang"
                      :editor="editor"
                      :buttons="buttons"
                      :cells="cells"
                      @input="select"
                    ></item>

                    <item
                      class="is-Child is-Grandchild"
                      v-for="(grandchild, index) in child.children"
                      :parent="item"
                      :item="grandchild"
                      :searchParams="searchParams"
                      :index="`grandchild-${index}`"
                      :key="`grandchild-${grandchild.id}`"
                      :selected="selected"
                      :lang="lang"
                      :editor="editor"
                      :buttons="buttons"
                      :cells="cells"
                      @input="select"
                    ></item>
                  </template>
                </tbody>
              </table>

              <div class="error" v-else-if="errors.length > 0">
                <p>Something went wrong! Please let our administrators know of the problems below.</p>

                <pre>{{ errors }}</pre>
              </div>

              <div
                class="error"
                v-else-if="items.resources.length === 0 && loading === false && $route.path.includes('submissions')"
              >
                <p>Sorry, seems there are no new user submissions matching your criteria.</p>
              </div>

              <div class="error" v-else-if="items.resources.length === 0 && loading === false">
                <p>Sorry, no content matched your criteria.</p>
                <p v-if="searchParams.search_query" class="error__Body">
                  There are no results for <strong>"{{ searchParams.search_query }}"</strong>.
                  <button class="button--XSmall button--Error button--Inline" @click="filter({ search_query: '' })">
                    Click here
                  </button>
                  to clear your search instead and show all results.
                </p>
              </div>
            </div>
          </div>
        </div>

        <transition name="slideUp">
          <actions
            v-show="this.selected.length > 0"
            :selectAll="selectAll"
            :selected="selected"
            :items="items.resources"
            :searchParams="searchParams"
          ></actions>
        </transition>
      </div>
      <nav class="pagination" v-if="pagination.pages > 1 && items.resources.length > 0">
        <button
          v-if="searchParams.offset > 0"
          @click="paginate({ offset: searchParams.offset - searchParams.limit }, searchParams, true)"
          class="pagination__PreviousPage"
        >
          &lt; {{ $store.getters.getStringTranslation('previous') }}
        </button>

        <ul class="pagination__Pages">
          <template v-if="pagination.current > 5">
            <li>
              <button
                @click="paginate({ offset: 0 }, searchParams, true)"
                :class="{ 'is-Active': pagination.current === 1 }"
              >
                1
              </button>
            </li>
            <li><a>...</a></li>
          </template>
          <template v-for="n in pagination.pages">
            <li :key="n.id" v-if="n < pagination.current + 5 && n > pagination.current - 5">
              <button
                @click="paginate({ offset: (n - 1) * searchParams.limit }, searchParams, true)"
                :class="{ 'is-Active': pagination.current === n }"
              >
                {{ n }}
              </button>
            </li>
          </template>
          <template v-if="pagination.current + 4 < pagination.pages">
            <li><a>...</a></li>
            <li>
              <button
                @click="paginate({ offset: (pagination.pages - 1) * searchParams.limit }, searchParams, true)"
                :class="{ 'is-Active': pagination.current === pagination.pages }"
              >
                {{ pagination.pages }}
              </button>
            </li>
          </template>
        </ul>

        <button
          v-if="pagination.current !== pagination.pages"
          @click="paginate({ offset: searchParams.offset + searchParams.limit }, searchParams, true)"
          class="pagination__NextPage"
        >
          {{ $store.getters.getStringTranslation('next') }} &gt;
        </button>
      </nav>
    </section>
  </div>
</template>

<script>
import { languages } from '@/router/globals.js'

import moment from 'moment'
import EventBus from '@/bus.js'

import PopHeader from '@/components/partials/Header.vue'
import Item from '@/components/listing/Item.vue'
import FilterType from '@/components/listing/FilterType.vue'
import FilterDate from '@/components/listing/FilterDate.vue'
import Actions from '@/components/listing/Actions.vue'
import Loader from '@/components/partials/Loader.vue'

import SelectAll from '@/components/mixins/SelectAll.js'
import FetchFeed from '@/components/mixins/FetchFeed.js'

export default {
  name: 'Listing',
  components: {
    PopHeader,
    Item,
    FilterType,
    FilterDate,
    Actions,
    Loader,
  },
  props: ['title', 'buttons', 'editor', 'cells', 'types'],
  mixins: [SelectAll, FetchFeed],
  data() {
    return {
      pagination: {
        pages: 1,
        current: 1,
      },
      router: [],
    }
  },
  watch: {
    $route(to, from) {
      this.router.from = from

      // Turn off action listeners upon route change
      EventBus.$off('actionDisable')
      EventBus.$off('actionFeature')
    },

    // Remove and update all posts when type changes
    types() {
      this.searchParams.offset = 0 // Reset offset on page change
      this.populateListing()
    },

    currentWebsite() {
      this.searchParams.api_keys = this.currentWebsite
    },

    // Remove and update all posts on search params change
    searchParams: {
      handler() {
        this.populateListing()
      },
      deep: true,
    },

    // Watch resources
    items: {
      handler() {
        this.countResources()
      },
      deep: true,
    },
  },
  computed: {
    // Compute item.resources as resources for SelectAll
    resources() {
      return this.items.resources
    },
    websites() {
      return this.$store.state.accounts.org.websites
    },
    apiKeys() {
      if (this.websites) return this.websites.map((website) => website.api_key)
      return null
    },
    currentWebsite() {
      return this.$store.state.websites.current
    },
    lang() {
      // TODO: Merge lang everywhere (most notably listing and tags, not sure if there are more instances)
      const query = this.$route.query
      if (query.search && JSON.parse(query.search).lang) return JSON.parse(query.search).lang
      if (this.searchParams && this.searchParams.lang) return this.searchParams.lang
      return this.$store.state.ui.lang
    },
    filters() {
      let filters = {
        featured: [
          { label: 'star', value: true, icon: true, weight: 'solid' },
          { label: 'star', value: false, icon: true, weight: 'light' },
        ],
        status: ['Published', 'Draft', 'Flagged', 'Disabled', 'Unlisted'],
        lang: languages,
        order_by: ['Updated', 'Inserted', 'Alphabetical'],
        order_dir: ['DESC', 'ASC'],
        date: [
          { label: 'All', start: null, end: null },
          { label: 'Today', start: moment().startOf('day'), end: moment().endOf('day') },
          { label: 'Past 7 days', start: moment().subtract(7, 'days'), end: moment() },
          { label: 'Past month', start: moment().subtract(1, 'months'), end: moment() },
        ],
      }

      if (this.$store.state.accounts.user.is_admin || this.$store.state.accounts.user.is_org_admin) {
        filters.status = ['Published', 'Draft', 'Flagged', 'Disabled', 'Unlisted', 'Password protected']
      }

      return filters
    },
    allStatuses() {
      return this.filters.status.map((item) => item.toLowerCase().trim().replace(/ /g, '_'))
    },
  },
  created() {
    let newObj = { types: this.types, lang: this.lang, status: this.allStatuses }
    let obj = this.searchParams
    let pushRoute = true

    // Check if search query exists, then replace search params with query
    if (this.$route.query.search !== undefined) {
      newObj = JSON.parse(this.$route.query.search)
    }

    // Use the vars, Luke!
    this.filter(newObj, obj, pushRoute)

    // Check if item has been updated from the editor, then replace the item data
    EventBus.$on('itemUpdated', (item) => {
      item = JSON.parse(item)
      const index = this.items.resources.findIndex((x) => x.id === item.id)
      this.$set(this.items.resources, index, item)
    })

    // Check if there's a new item, then put that item on top of items
    EventBus.$on('itemAdded', (item) => {
      item = JSON.parse(item)
      this.items.resources.unshift(item)
    })

    // Remove disabled items from listing
    EventBus.$on('itemDisable', (item) => {
      item = JSON.parse(item)
      let index = this.items.resources.findIndex((x) => x.id === item.id)

      this.items.resources.splice(index, 1)
      this.selected = []
      index = ''
    })
  },
  methods: {
    removeFilter(type) {
      delete this.searchParams[type]
      this.filter({}, this.searchParams, true, true, true)
    },

    // Push/replace new objects into the filter parameter in searchParams
    filter(newObj, obj = this.searchParams, pushRoute = true, fetch = false, featured = true) {
      if (featured) this.$set(this.searchParams, 'is_featured', this.searchParams.is_featured)

      this.selected = []
      Object.keys(newObj).forEach(function (key) {
        obj[key] = newObj[key]
      })

      // Add route URL parameters
      if (pushRoute === true) {
        const urlParams = JSON.stringify(obj)
        this.$router.push({ query: { search: urlParams } })
      }

      if (fetch === true) this.fetchFeed()
    },

    changeLanguage($event) {
      this.filter({ lang: $event.target.value })
    },

    countResources() {
      this.pagination.pages = 1
      // Count resources upon creation

      let summed = 0
      const type = Object.keys(this.items.counts)[0]

      if (typeof this.items.counts[type] === 'object') {
        summed = this.items.counts[type].all
      } else {
        for (var key in this.items.counts) {
          summed += this.items.counts[key]
        }
      }
      this.resourceCount = summed
      this.pagination.pages = Math.round(Math.ceil(summed / 10))
      this.pagination.current = this.searchParams.offset / this.searchParams.limit + 1
    },

    paginate(newObj, obj = this.searchParams, pushRoute = true) {
      window.scrollTo(0, 0)
      this.filter(newObj, obj, pushRoute)
    },

    populateListing() {
      // Reset list on load
      this.items.resources = []

      // Define defaults for each listing view (e.g. submissions, news, campaigns, ...) but add a specific parameter to submissions
      let listingParams = {
        types: this.types,
        lang: this.searchParams.lang,
        status: this.searchParams.status,
        frontend_submissions: false,
      }
      if (this.$route.path.includes('submissions'))
        listingParams = {
          types: this.types,
          lang: this.searchParams.lang,
          status: this.searchParams.status,
          frontend_submissions: true,
        }
      this.filter(listingParams, this.searchParams, true, true)
    },
  },
}
</script>
